"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flattenArray = void 0;
exports.filterNullish = filterNullish;
exports.mapNotNullish = mapNotNullish;
exports.range = range;
exports.upsert = upsert;
exports.updateObject = updateObject;
exports.matchBy = matchBy;
exports.upsertById = upsertById;
exports.arrayToObjectByKey = arrayToObjectByKey;
exports.arrayIsEqual = arrayIsEqual;
exports.dedupBy = dedupBy;
exports.dedup = dedup;
exports.without = without;
exports.reverse = reverse;
exports.max = max;
exports.min = min;
exports.arrayToObject = arrayToObject;
exports.partition = partition;
exports.removeAtIndex = removeAtIndex;
exports.insertAtIndex = insertAtIndex;
const ramda_1 = require("ramda");
const objectOperations_1 = require("./objectOperations");
const _ = require("lodash");
function filterNullish(array) {
    return (0, ramda_1.reject)(ramda_1.isNil, array);
}
function mapNotNullish(arr, fun) {
    const newArr = arr.map(fun);
    return filterNullish(newArr);
}
/**
 *
 * @param n
 * @returns an array from 0 to n-1
 */
function range(n) {
    return [...Array(n).keys()];
}
/**
 * Replace the first element in the array which matches the item using the matching function
 * @param matchingFn A function used to match the item to an element in the array
 * @param item the item to upsert into the array
 * @param array the array to upsert
 * @returns a new array with the item upserted. Does not mutate the original array.
 */
function upsert(matchingFn, item, array = []) {
    const newArray = (0, objectOperations_1.deepCopy)(array);
    const index = newArray.findIndex(val => matchingFn(val, item));
    if (index === -1) {
        newArray.push(item);
    }
    else {
        newArray[index] = item;
    }
    return newArray;
}
function updateObject(matchingFn, item, array = []) {
    const newArray = (0, objectOperations_1.deepCopy)(array);
    const index = newArray.findIndex(val => matchingFn(val));
    if (index !== -1) {
        newArray[index] = { ...newArray[index], ...item };
    }
    return newArray;
}
function matchBy(key) {
    return (a, b) => a[key] === b[key];
}
/**
 * Replace the first element in the array which matches the id of the item passed in.
 * @param item the item to upsert into the array
 * @param array the array to upsert
 * @returns a new array with the item upserted. Does not mutate the original array.
 * @see upsert
 */
function upsertById(item, array = []) {
    const matchById = matchBy('id');
    return upsert(matchById, item, array);
}
/**
 * Converts an array to an object containing each item in the array, keyed by the value of object[key]
 * TODO: Refine type definition for ObjectFromArray
 * @param array
 * @param key
 * @returns
 */
function arrayToObjectByKey(array, key) {
    const initial = {};
    // current: any to avoid "Type 'keyof AllOfType<T, string>' cannot be used to index type 'T'" error
    return array.reduce((obj, current) => {
        const currentKey = current[key];
        return { ...obj, [currentKey]: current };
    }, initial);
}
function arrayIsEqual(x, y) {
    return _(x).xorWith(y, _.isEqual).isEmpty();
}
/**
 * Keeps the first value matched, removes the rest
 * @param fn returns the value to dedup by
 * @param array
 * @returns a new array with duplicates removed
 */
function dedupBy(fn, array) {
    return (0, ramda_1.uniqBy)(fn, array);
}
/**
 *
 * @param array
 * @returns a new array with duplicates removed
 */
function dedup(array) {
    return (0, ramda_1.uniq)(array);
}
/**
 * Removes values in first array from second array
 * Does not mutate the original array
 * @param without
 * @param array
 * @returns
 */
function without(without, array) {
    return (0, ramda_1.without)(without, array);
}
function reverse(array) {
    return (0, ramda_1.reverse)(array);
}
exports.flattenArray = _.flatten;
function max(array) {
    return array.reduce((currMax, currVal) => (currMax > currVal ? currMax : currVal));
}
function min(array) {
    return array.reduce((currMax, currVal) => (currMax < currVal ? currMax : currVal));
}
function arrayToObject(arr, value) {
    return arr.reduce((previous, current) => {
        previous[current] = value(current);
        return previous;
    }, {});
}
/**
 * Partitions an array into two based on a condition
 *
 * @example
 * partition([1, 2, 3, 4, 5, 6], number => number % 2 === 0)
 * // expect [[2, 4, 6], [1, 3, 5]]
 * @returns
 */
function partition(arr, condition) {
    return arr.reduce((previous, current) => {
        const [pass, fail] = previous;
        if (condition(current)) {
            pass.push(current);
        }
        else {
            fail.push(current);
        }
        return previous;
    }, [[], []]);
}
/**
 * Removes an item from a specific index in an array and returns both the updated array.
 * It does not mutate the original array.
 *
 * @param {T[]} array - The array to remove the item from.
 * @param {number} index - The index of the item to remove.
 * @returns {T[]} The updated array.
 *
 * @example
 * const array = [1, 2, 3, 4];
 * const result = removeAtIndex(array, 2);
 * console.log(result); // [1, 2, 4]
 */
function removeAtIndex(array, index) {
    return [...array.slice(0, index), ...array.slice(index + 1)];
}
/**
 * Adds an item at a specific index in an array and returns the updated array.
 * It does not mutate the original array.
 *
 * @param {T[]} array - The array to remove the item from.
 * @param {number} index - The index of the item to remove.
 * @param {T} item - The item to insert.
 * @returns {T[]} The updated array.
 *
 * @example
 * const array = [1, 2, 3, 4];
 * const result = insertAtIndex(array, 2, 4);
 * console.log(result); // [1, 2, 4, 3, 4]
 */
function insertAtIndex(array, index, item) {
    return [...array.slice(0, index), item, ...array.slice(index)];
}
